import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ConsultationModal from "@/components/ConsultationModal";
import { Button } from "@/components/ui/button";
import { Search, HelpCircle, ArrowRight, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default FAQs - will be overridden by CMS data
const defaultFAQs = {
  general: [
    { q: "What is Medical Tourism?", a: "Medical tourism refers to traveling to another country for medical treatment. India has become a leading destination due to world-class hospitals, experienced doctors, and significantly lower costs compared to Western countries." },
    { q: "Why choose India for medical treatment?", a: "India offers: 1) World-class JCI/NABH accredited hospitals, 2) English-speaking doctors trained at international institutions, 3) 60-80% cost savings compared to US/UK, 4) No waiting times for procedures, 5) Latest medical technology and treatments." },
    { q: "How does Paxgrow help international patients?", a: "Paxgrow provides end-to-end facilitation including: hospital selection, doctor appointments, treatment planning, visa assistance, airport pickup, accommodation, local transport, post-treatment care coordination, and 24/7 support throughout your journey." },
    { q: "Is it safe to get treatment in India?", a: "Yes, India has some of Asia's best hospitals with international accreditations (JCI, NABH). Our partner hospitals maintain strict hygiene protocols, employ internationally trained surgeons, and use the latest medical equipment. We only recommend hospitals with proven track records." },
  ],
  costs: [
    { q: "How much can I save on treatment in India?", a: "Patients typically save 60-80% compared to the same procedures in the US, UK, or other Western countries. For example, a heart bypass surgery costing $100,000+ in the US may cost $8,000-$15,000 in India with similar quality outcomes." },
    { q: "What is included in the treatment cost?", a: "Treatment packages typically include: surgery/procedure charges, hospital stay, doctor fees, medications during hospitalization, and basic diagnostic tests. Additional costs may include extended stay, companion accommodation, and specific medications." },
    { q: "Do you accept insurance?", a: "Yes, we work with various international insurance providers. We help with pre-authorization, documentation, and claim processing. Contact us with your insurance details for a personalized assessment." },
    { q: "Are there hidden costs?", a: "We believe in complete transparency. We provide detailed cost breakdowns upfront covering all expected expenses. Any additional tests or treatments are discussed and approved by you before proceeding." },
  ],
  treatment: [
    { q: "How do I choose the right hospital?", a: "We evaluate hospitals based on: accreditation status, specialist expertise, success rates, patient reviews, infrastructure, and international patient services. We then recommend options best suited to your specific medical needs and budget." },
    { q: "Can I get a second opinion?", a: "Absolutely. We encourage patients to seek second opinions. Send us your medical reports and we'll arrange consultations with relevant specialists - often at no additional cost for the initial review." },
    { q: "How long will I need to stay in India?", a: "Stay duration depends on your procedure. Minor procedures: 7-10 days. Major surgeries: 2-4 weeks. Complex treatments: 4-8 weeks. We provide specific timelines after reviewing your case." },
    { q: "What if there are complications after returning home?", a: "We maintain contact with patients post-treatment and coordinate with your local healthcare providers if needed. Many of our partner hospitals offer tele-consultation services for follow-up care." },
  ],
  visa: [
    { q: "Do I need a medical visa?", a: "Yes, for treatment in India, you'll need a Medical Visa (M-Visa). We provide complete assistance with the application process including invitation letters from hospitals." },
    { q: "How long does the visa process take?", a: "Medical visa processing typically takes 5-10 working days. For urgent cases, we can help expedite the process. Emergency visas can be processed within 2-3 days in some cases." },
    { q: "Can family members accompany me?", a: "Yes, up to 2 family members can obtain Medical Attendant Visas (MX-Visa) to accompany the patient. We help with documentation for attendant visas as well." },
  ],
  travel: [
    { q: "Do you provide airport pickup?", a: "Yes, we arrange airport pickup and drop-off services. Our representative meets you at the airport and ensures comfortable transportation to your accommodation or hospital." },
    { q: "Where will I stay during treatment?", a: "We offer options ranging from hospital guest houses to hotels near the hospital. For longer stays, we can arrange serviced apartments. All accommodations are vetted for comfort and proximity to the hospital." },
    { q: "What about language barriers?", a: "English is widely spoken in Indian hospitals. Our team provides multilingual support, and we can arrange interpreters for patients who prefer communication in other languages." },
  ],
};

const categories = [
  { id: "general", label: "General Questions", icon: HelpCircle },
  { id: "costs", label: "Costs & Insurance", icon: HelpCircle },
  { id: "treatment", label: "Treatment Process", icon: HelpCircle },
  { id: "visa", label: "Visa & Documentation", icon: HelpCircle },
  { id: "travel", label: "Travel & Stay", icon: HelpCircle },
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState(defaultFAQs);
  const [activeCategory, setActiveCategory] = useState("general");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Try to fetch FAQs from API (CMS managed)
    axios.get(`${API}/faqs`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setFaqs(r.data);
      }
    }).catch(() => {
      // Use default FAQs if API fails
    });
  }, []);

  // Filter FAQs based on search
  const filteredFAQs = searchQuery
    ? Object.values(faqs).flat().filter(
        faq => faq.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
               faq.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs[activeCategory] || [];

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-600/10 text-orange-600 border-orange-600/20 mb-4 font-body text-xs uppercase tracking-wider">
            Help Center
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4">
            Frequently Asked <span className="text-orange-600">Questions</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Find answers to common questions about medical tourism in India
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg rounded-full border-slate-200 focus:border-orange-500"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {!searchQuery && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? "bg-orange-600 text-white shadow-lg shadow-orange-600/30"
                      : "bg-white text-slate-600 border border-slate-200 hover:border-orange-300"
                  }`}
                  data-testid={`faq-category-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          {searchQuery && (
            <div className="mb-8 text-center">
              <p className="text-slate-500">
                Showing {filteredFAQs.length} results for "<span className="text-orange-600 font-medium">{searchQuery}</span>"
              </p>
              <button 
                onClick={() => setSearchQuery("")}
                className="text-orange-600 hover:underline text-sm mt-2"
              >
                Clear search
              </button>
            </div>
          )}

          <Card className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <Accordion type="single" collapsible className="divide-y divide-slate-100">
              {filteredFAQs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                  <AccordionTrigger className="px-6 py-5 text-left hover:no-underline hover:bg-slate-50 transition-colors" data-testid={`faq-item-${i}`}>
                    <span className="font-heading text-base font-medium text-slate-900 pr-4">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 pt-0">
                    <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No matching questions found.</p>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="h-12 w-12 text-orange-600 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
            Still Have Questions?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Can't find what you're looking for? Our medical tourism experts are here to help you with any questions about treatment in India.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ConsultationModal
              trigger={
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 font-semibold">
                  Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
            <a href="https://wa.me/917289007800" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 font-semibold">
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
